import errorCodes from "@/apis/error";
import { categoryFetch, categorySetItem, categoryUpdateItem, CategoryUpdateKey } from "@/apis/product";
import { ProductCategory } from "@/types/product";
import { notification } from "antd";
import { Dispatch, SetStateAction, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

type Fetch = (id: string) => void;
type UpdateItem = (key: CategoryUpdateKey, value: string) => Promise<void>;
type SetItemOrgs = (values: string[]) => Promise<void>;

type IUseProductCategory = () => [
  ProductCategory | undefined, boolean, {
    fetchDetail: Fetch,
    updateItem: UpdateItem,
    setItemOrgs: SetItemOrgs,
    setDetail: Dispatch<SetStateAction<ProductCategory | undefined>>,
    setLoading: (loading: boolean) => void
  }
]

const useProductCategory: IUseProductCategory = () => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<ProductCategory | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // ✅ 组件卸载时取消请求
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchDetail: Fetch = (id) => {
    // ✅ 取消上一次请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // ✅ 创建新的 AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true)
    categoryFetch(id, controller)
      .then((res) => setDetail(res))
      .catch((err) => {
        // ✅ 忽略取消的请求
        if (err?.name === 'AbortError') {
          return;
        }
      })
      .finally(() => setLoading(false))
  }

  const updateItem: UpdateItem = (key, value) => {
    return new Promise((resolve, reject) => {
      if (detail && value !== detail[key]) {
        categoryUpdateItem(detail.id, key, value)
          .then(() => {
            setDetail({ ...detail, [key]: value });
            notification.success({ placement: 'top', message: t('product.modifySuccess') })
            resolve();
          })
          .catch((e) => {
            notification.error({ placement: 'top', message: t('product.modifyFailed'), description: errorCodes[e] || e })
            reject();
          })
      }
    })
  }

  const setItemOrgs: SetItemOrgs = (values) => {
    return new Promise((resolve, reject) => {
      categorySetItem(detail?.id ?? '', values)
        .then(() => resolve())
        .catch((err) => reject(err))
    })
  }

  return [detail, loading, { fetchDetail, updateItem, setItemOrgs, setDetail, setLoading }]
}

export default useProductCategory;